import { CheckCircleIcon } from '@heroicons/react/16/solid';
import React, { useEffect, useRef } from 'react';
import { ErrorAlert } from './common/Custom/ErrorAlert';

type AlertType = 'error' | 'success';

interface ShowAlertProps {
  alertType: AlertType;
  alertTitle: string;
  alertMessage: string;
  isVisible: boolean;
  onClose: () => void;
  /** How long a **success** stays up. Errors ignore it — see below. */
  timeout?: number;
  isInline?: boolean;
}

/**
 * The older alert API, kept so its ~100 call sites did not all have to
 * move at once. New code uses `ErrorAlert` directly.
 *
 * **An error renders `ErrorAlert` and stays until dismissed (#652).** It
 * used to time out like a success: a detail page whose body renders only
 * once the record loads was left blank with nothing saying why, and the
 * nine screens passing `timeout={0}` closed the error the instant it
 * opened. A success still fades — it reports something that worked.
 */
const ShowAlert: React.FC<ShowAlertProps> = ({
  alertType = 'error',
  alertTitle,
  alertMessage,
  isVisible,
  onClose,
  timeout = 5000,
  isInline = false,
}) => {
  // Most callers pass `onClose` as an inline arrow, which is a new
  // function every render; keeping it out of the effect's dependencies
  // stops each re-render restarting the timer.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const autoClose = isVisible && alertType === 'success';

  useEffect(() => {
    if (!autoClose) return undefined;
    const timeoutId = setTimeout(() => onCloseRef.current(), timeout);
    return () => clearTimeout(timeoutId);
  }, [autoClose, timeout]);

  if (!isVisible) return null;

  const containerClasses = isInline
    ? 'w-full my-2'
    : 'fixed top-4 right-4 z-50 max-w-sm';

  if (alertType !== 'success') {
    return (
      <div className={containerClasses}>
        <ErrorAlert title={alertTitle} onDismiss={onClose}>
          {alertMessage}
        </ErrorAlert>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div
        role="status"
        className="flex items-start gap-2 rounded-md bg-success/10 px-3 py-2 text-[13px]/5 text-success"
      >
        <CheckCircleIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          {alertTitle && <p className="font-medium">{alertTitle}</p>}
          {alertMessage && <p className="break-words">{alertMessage}</p>}
        </div>
      </div>
    </div>
  );
};

export default ShowAlert;
